import { gsap } from 'gsap';

class Accordion {
    constructor(group) {
        this.config = {
            rotationInterval: 4000,
            pauseOnHover: true,
            pauseOnFocus: true,
            allowAllClosed: false,
        };

        this.group = group;
        this.elements = this.getElements();

        this.initialize();
    }

    initialize() {
        this.setupEventListeners();
    }

    getElements() {
        return {
            btns: this.group.querySelectorAll('.js-accordion-btn'),
            content: this.group.querySelectorAll('.js-accordion-content'),
        };
    }

    setupEventListeners() {
        const { btns, content } = this.elements;

        btns.forEach((btn, index) => {
            // Click handler
            btn.addEventListener('click', (event) => {
                event.preventDefault();

                const isOpen = btn.getAttribute('aria-expanded') === 'true';
                const contentPanel = content[index];

                if (!isOpen) {
                    this.open(contentPanel, btn);
                    btn.setAttribute('aria-expanded', 'true');
                } else {
                    const allowAllClosed = this.group.dataset.accordionAllowAllClosed !== 'false';
                    if (allowAllClosed) {
                        this.close(contentPanel, btn);
                        btn.setAttribute('aria-expanded', 'false');
                    }
                }
            });

            // Keyboard navigation
            btn.addEventListener('keydown', (event) => {
                this.handleKeydown(event, btn, index);
            });
        });
    }

    close(content, btn) {
        if (!content || !btn) return;

        const innerContent = content.querySelector('.js-accordion-inner-content');
        if (!innerContent) return;

        gsap.to(content, {
            duration: 0.3,
            height: 0,
            display: 'none',
            onComplete: () => {
                content.setAttribute('hidden', '');
                this.updateScreenReaderText(btn, false);
            },
        });
    }

    open(content, btn) {
        if (!content || !btn) return;

        const { btns, content: contents } = this.elements;

        // Close other open accordions
        btns.forEach((otherBtn, index) => {
            if (otherBtn === btn) return;
            if (otherBtn.getAttribute('aria-expanded') === 'true') {
                this.close(contents[index], otherBtn);
                otherBtn.setAttribute('aria-expanded', 'false');
            }
        });

        content.removeAttribute('hidden');
        this.updateScreenReaderText(btn, true);

        gsap.set(content, {
            display: 'block',
            height: 'auto',
        });

        const innerContent = content.querySelector('.js-accordion-inner-content');
        if (!innerContent) return;

        gsap.from(content, {
            duration: 0.3,
            height: 0,
        });
    }

    handleKeydown(event, btn, index) {
        const { btns } = this.elements;
        const totalButtons = btns.length;

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                btns[(index + 1) % totalButtons]?.focus();
                break;
            case 'ArrowUp':
                event.preventDefault();
                btns[(index - 1 + totalButtons) % totalButtons]?.focus();
                break;
            case 'Home':
                event.preventDefault();
                btns[0]?.focus();
                break;
            case 'End':
                event.preventDefault();
                btns[totalButtons - 1]?.focus();
                break;
            // no default
        }
    }

    // eslint-disable-next-line class-methods-use-this
    updateScreenReaderText(btn, isOpen) {
        const srOnly = btn.querySelector('.sr-only');
        if (srOnly) {
            srOnly.textContent = isOpen ? 'Click to close' : 'Click to open';
        }
    }
}

// eslint-disable-next-line import/prefer-default-export
export { Accordion };
